<template lang="pug">
.wrap
  page-title(fontSize='30px') Import new users to My Ethos

  transition(name='el-fade-in' mode='out-in')
    div(v-if='!completed')
      p Select which type of users you're uploading.
      p If you require different users to have different roles you should split the CSV out to have one file per user type.

      .seats
        seats-assigner(v-model='importData.appLicenseGroupIds' ref='seatsAsssigner')

      .section
        page-title(fontSize='30px') Upload your CSV file

        p Select your CSV file here.
        p
          | Need help?
          a(href="https://s3-eu-west-1.amazonaws.com/myethos-staging/assets/admin/MyEthosUserImportExample.csv" target="_blank") Download our CSV
          |  import file template and replace the examples with your own data.

        .uploader
          el-upload.uploader(:multiple='false' drag action="none" :on-preview='onFilePreview'
            :http-request='handleFileUpload' :on-remove='onRemoveFile'
            :file-list='fileList' accept='text/csv')

            i.el-icon-upload
            .el-upload__text Drop file here or click to upload
            el-progress(v-if='this.uploadPercentage' :percentage="this.uploadPercentage")

      .section
        page-title(fontSize='30px') Do our titles match your columns?

        .placeholder(v-if='!hasUploadedFile') Please upload a CSV file first.

        .csv-preview(v-else)
          .csv-preview-options
            el-checkbox(v-model="previewOptions.hasHeader") Skip first row (header)

          .columns(v-loading='loadingCsv')
            .column(v-for='(col, colIndex) in csvPreviewData' :key='colIndex')
              .column-header
                .column-field
                  el-select(v-model='importData.columnMapping[colIndex]' clearable placeholder='Ignore')
                    el-option(v-for='option in colOptions[colIndex]' :value='option.value'
                      :label='option.label' :key='option.value' :disabled='option.disabled')
                .column-options(v-if='importData.columnMapping[colIndex] === "phone_num"')
                    i.field-trigger.el-icon-setting(slot='reference' @click='phoneDialogVisible = true')

                    el-dialog(title="Phone number options" :visible.sync="phoneDialogVisible" size="tiny")
                      el-form
                        el-form-item(label="Default international prefix" prop="phoneNum")
                          el-input.phone-num(v-model.trim="importData.mappingOptions.phoneNum.prefix" placeholder='Example: +44')

                      div(slot="footer" class="dialog-footer")
                        el-button(@click="phoneDialogVisible = false") Done

              .column-list
                .column-list-item(v-for='value in col' :key='value') {{ value }}

      .section(v-if='hasUploadedFile && unmatchedColumns.length')
        | Please match the following columns before continuing:
        .unmatched-cols
          el-tag.unmatched-col(v-for='col in unmatchedColumns' :key='col.value' type='gray') {{ col.label }}

      .section
        .subsection
          strong If the CSV file contains entries for email addresses already registered in the system:
        .subsection
          el-radio(v-model="importData.onDuplicate" label="merge_groups") Update the users and add into the new permission groups assigned above
        .subsection
          el-radio(v-model="importData.onDuplicate" label="ignore") Ignore duplicates in the CSV file

      .section
        el-button(type='primary' @click='submit' :disabled='!canSubmit') Import Users

    .completed(v-else)
      | We are processing your file, you will receive an email when the import is completed.

</template>

<script>
import _ from "lodash";
import { mapActions } from "vuex";
import CrudMixin from "@/shared/mixins/Crud";
import PageTitle from "@/shared/components/layout/PageTitle";
import Papa from "papaparse";
import SeatsAssigner from "@/shared/components/SeatsAssigner";

export default {
  mixins: [CrudMixin],
  components: { PageTitle, SeatsAssigner },
  data() {
    const allOptions = [
      { label: "First name", value: "first_name" },
      { label: "Surname", value: "last_name" },
      { label: "Email address", value: "email" },
      { label: "Phone num", value: "phone_num" },
      { label: "Job role", value: "job_role" }
    ];

    return {
      completed: false,
      loadingCsv: false,
      appLicenses: [],
      uploadPercentage: 0,
      fileList: [],
      csvFile: null,
      importData: {
        appLicenseGroupIds: [],
        columnMapping: [],
        mappingOptions: {
          phoneNum: {
            prefix: ""
          }
        },
        onDuplicate: "merge_groups"
      },
      phoneDialogVisible: false,
      previewOptions: {
        hasHeader: true
      },
      numOfCsvColumns: 0,
      csvPreviewData: [],
      loading: false,
      allOptions
    };
  },
  computed: {
    columns() {
      return this.csvPreviewData.map(row => ({
        data: [...row]
      }));
    },
    colOptions() {
      return _.range(this.numOfCsvColumns).map((o, colIndex) =>
        this.allOptions.map(opt => {
          const alreadyUsedForIndex = this.importData.columnMapping.indexOf(
            opt.value
          );
          return {
            ...opt,
            disabled:
              alreadyUsedForIndex !== -1 && alreadyUsedForIndex !== colIndex
          };
        })
      );
    },
    hasUploadedFile() {
      return !!this.csvFile;
    },
    unmatchedColumns() {
      return this.allOptions.filter(
        opt => this.importData.columnMapping.indexOf(opt.value) === -1
      );
    },
    canSubmit() {
      return !this.unmatchedColumns.length;
    }
  },
  watch: {
    "previewOptions.hasHeader": function watchHasHeader() {
      this.parseCsv();
    }
  },
  methods: {
    ...mapActions({
      fetchAppLicenses: "appLicenses/fetchAppLicenses",
      uploadFile: "upload/uploadFile",
      createUserImport: "users/createUserImport"
    }),
    onUploadProgress(percentage) {
      this.uploadPercentage = percentage;
    },
    onFilePreview({ url }) {
      window.open(url);
    },
    onRemoveFile() {
      this.csvFile = null;
    },
    handleFileUpload({ file }) {
      this.csvFile = file;
      this.parseCsv();
    },
    parseCsv() {
      const hasHeader = this.previewOptions.hasHeader;
      this.loadingCsv = true;
      Papa.parse(this.csvFile, {
        skipEmptyLines: true,
        header: this.previewOptions.hasHeader,
        complete: results => {
          const normalisedResults = hasHeader
            ? _.map(results.data, row => _.values(row))
            : results.data;
          this.numOfCsvColumns = hasHeader
            ? results.meta.fields.length
            : _.get(results.data, "0.length", 0);
          this.csvPreviewData = _.unzip(normalisedResults);
          this.importData.columnMapping = hasHeader
            ? results.meta.fields.map(() => null)
            : _.range(this.numOfCsvColumns).map(() => null);
          this.loadingCsv = false;
        }
      });
    },
    submit() {
      this.showPageLoader();
      this.uploadFile({
        file: this.csvFile,
        bucketKey: "user_imports",
        options: { onProgress: this.onUploadProgress }
      })
        .then(({ path }) => {
          const importData = {
            attachmentPath: path,
            params: {
              appLicenseGroupIds: this.importData.appLicenseGroupIds,
              columnMapping: this.importData.columnMapping,
              mappingOptions: this.importData.mappingOptions,
              hasHeader: this.previewOptions.hasHeader,
              onDuplicate: this.importData.onDuplicate
            }
          };
          return this.createUserImport({ data: importData });
        })
        .then(() => {
          this.hidePageLoader();
          this.completed = true;
        })
        .catch(err => {
          this.hidePageLoader();
          throw err;
        });
    }
  },
  created() {
    this.fetchAppLicenses().then(data => {
      this.appLicenses = data;
    });
  }
};
</script>

<style scoped lang="scss">
.seats {
  margin-top: 30px;
}

.section {
  border-top: 1px solid #ccc;
  margin-top: 30px;
  padding-top: 30px;
}

.subsection {
  margin-bottom: 10px;
}

.uploader {
  margin-top: 30px;
}

.columns {
  display: flex;
  margin-top: 20px;
  overflow-x: auto;
  overflow-y: auto;
  max-height: 500px;
}

.column {
  flex: 0 0 200px;
  margin-right: 10px;
}

.column-header {
  margin-bottom: 10px;
  display: flex;
}

.column-field {
  flex: 1;
}

.column-options {
  flex: 0 0 auto;
  padding-left: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.column-list {
  background: white;
  border: 1px solid #ddd;
}

.column-list-item {
  padding: 10px;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: 0;
  }
}

.uploader {
  :global(.el-upload) {
    display: block;
  }
  :global(.el-upload-dragger) {
    width: 100%;
    border: 1px solid #ddd;
    padding: 0 40px;
    border-radius: 2px;
  }
}

.completed {
  background: white;
  border: 1px solid #ddd;
  padding: 50px;
  text-align: center;
  font-size: 20px;
  color: #666;
}

.unmatched-cols {
  margin-top: 5px;
}

.unmatched-col {
  display: inline-block;
  margin-right: 10px;
}

.mapping-popover {
  width: 200px;
}
</style>
